import { request } from './core'

/** A Partner Central benefit application — a fund request or a cash claim —
 *  read LIVE (people edit them in the console; we only store the linked id). */
export interface BenefitApplication {
  id: string
  benefit_id: string
  name: string
  /** PENDING_SUBMISSION | IN_REVIEW | ACTION_REQUIRED | APPROVED | REJECTED | CANCELED */
  status: string
  stage: string
  status_reason: string
  activity_name: string
  amount: string | null
  start_date: string | null
  end_date: string | null
  opportunity_ids: string[]
  /** Only PENDING_SUBMISSION accepts edits (documents included). */
  editable: boolean
  files: { name: string; label: string; status: string; status_reason: string; created_at: string }[]
  updated_at: string
}

export type DocumentState = 'accepted' | 'pending' | 'failed' | 'wrong_label' | 'missing'

export interface DocumentCheck {
  /** The exact Partner Central label (BusinessUseCase) AWS reviews it under. */
  label: string
  state: DocumentState
  file_name: string
  detail: string
}

/** One linked record as the assessment endpoint returns it: unlinked, the live
 *  record, or the error that read hit (one bad id never hides the other). */
export type LinkedApplication =
  | { linked: false }
  | ({ linked: true; error?: undefined; documents?: DocumentCheck[] } & BenefitApplication)
  | { linked: true; id: string; error: string }

export interface AssessmentFunding {
  fund_request: LinkedApplication
  claim: LinkedApplication
  signed_signoff_available: boolean
}

export type ClaimDocument = 'signoff' | 'report'
export type FundingProgram = 'ai' | 'wafr'

export function liveApplication(
  link: LinkedApplication | undefined,
): (BenefitApplication & { documents?: DocumentCheck[] }) | null {
  return link && link.linked && !link.error ? (link as BenefitApplication) : null
}

/** A fund request AWS has accepted for review or approved — i.e. really filed. */
export function isFiled(app: BenefitApplication | null): boolean {
  return app != null && ['IN_REVIEW', 'APPROVED'].includes(app.status)
}

const pc = '/api/partner-central'

export const partnerCentralApi = {
  /** The fund requests that reference this ACE opportunity (the finder). */
  findFundRequests: (opportunityId: string, program: FundingProgram) =>
    request<{ fund_requests: BenefitApplication[] }>(
      `${pc}/opportunities/${encodeURIComponent(opportunityId)}/fund-requests?program=${program}`,
    ),

  getBenefitApplication: (id: string) =>
    request<{ application: BenefitApplication }>(`${pc}/applications/${encodeURIComponent(id)}`),

  /** Cash claims drawn on a fund request (empty until it's approved). */
  findClaims: (fundRequestId: string) =>
    request<{ claims: BenefitApplication[] }>(
      `${pc}/applications/${encodeURIComponent(fundRequestId)}/claims`,
    ),

  getAssessmentFunding: (slug: string) => request<AssessmentFunding>(`/api/assessments/${slug}/funding`),

  /** Upload the signed sign-off and/or the report to the linked claim — a
   *  Partner Central write, so `revision` (If-Match) is checked first. */
  attachClaimDocuments: (slug: string, documents: ClaimDocument[], revision: number) =>
    request<{ claim: LinkedApplication }>(
      `/api/assessments/${slug}/funding/claim/attach`,
      { method: 'POST', body: JSON.stringify({ documents }) },
      { ifMatch: revision },
    ),
}
